import { PureQueryOptions } from '@apollo/client';

import { MyCommsCountersDocument } from 'core/graphql';
import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

import { MCP_ACCESS_ROLES, visibleFor } from '../Palette/paletteRoles';

/**
 * Кому сервер отдаёт `myCommsCounters` — зеркало `MANAGEMENT_ROLES` бэкенда
 * (Admin, AccountManager, SalesManager, HRManager, FinanceManager). Та же
 * константа, что у «Подключённых ИИ» (`MCP_ACCESS_ROLES`): своя копия
 * списка разъехалась бы с оригиналом. Остальным запрос не шлём — отказ
 * сервера лёг бы ошибкой в консоль на каждом экране.
 */
export const MY_COMMS_COUNTERS_ROLES: UserRole[] = MCP_ACCESS_ROLES;

/** Элемент `refetchQueries`: имя операции или документ с переменными. */
export type TCounterRefetch = string | PureQueryOptions;

/**
 * Счётчики рейки, которые одно чтение или одна обработка диалога обязаны
 * обновить СРАЗУ, а не через 60 с опроса (ТЗ 0096). Их строки — без `id`, ни
 * один ответ мутации их не пропатчит, поэтому — refetch по имени.
 *
 * ⚠️ Это ИМЕНА ОПЕРАЦИЙ, а не полей: Apollo сверяет строку с `queryName`
 * смонтированных запросов и на промахе молчит. Сверку держит
 * `bulkTriageRefetch.test.ts` (имя константы содержит `AGGREGATE_QUERIES`
 * ровно для того, чтобы тест её видел).
 *
 * `MyCommsCounters` здесь БОЛЬШЕ НЕТ (перепроверка F47): Apollo 3.3.6
 * перечитывает по имени и skip-нутые экземпляры. Меню монтирует счётчики у
 * всех ролей, а у роли без права запрос стоит в `skip` — refetch по имени
 * уходил на сервер, отказ отклонял промис, и при `awaitRefetchQueries`
 * успешная мутация (закрыть задачу у методиста) кончалась тостом «ошибка».
 * Счётчики меню перечитываются документом и только у ролей с правом —
 * `commsCounterRefetch()`.
 */
export const COUNTER_AGGREGATE_QUERIES: string[] = ['GroupsOverviewList'];

/** Положены ли роли счётчики меню; без аргумента — роли текущего входа. */
export const canReadCommsCounters = (roles?: readonly string[]): boolean =>
  visibleFor(
    [...(roles ?? userStateVar().me?.roles ?? [])] as UserRole[],
    MY_COMMS_COUNTERS_ROLES,
  );

/**
 * Перечитать счётчики меню: документом (не по имени — см. выше) и только
 * если роли они положены. Роли читаются В МОМЕНТ вызова, поэтому функция, а
 * не константа.
 */
export const myCommsCountersRefetch = (): PureQueryOptions[] =>
  canReadCommsCounters() ? [{ query: MyCommsCountersDocument }] : [];

/**
 * Счётчики рейки и меню — для мутаций чтения, обработки и задач (просрочка
 * в шапке меняется от любой из них).
 */
export const commsCounterRefetch = (): TCounterRefetch[] => [
  ...COUNTER_AGGREGATE_QUERIES,
  ...myCommsCountersRefetch(),
];

/** Aggregates that no mutation payload can patch, refetched by OPERATION name
 *  so every mounted instance updates whatever its variables are. */
const BULK_AGGREGATE_QUERIES = [
  'DashboardConversations', // conversationsBrowser — queue membership changed
  'GroupMessagesFeed', // groupMessages(unreadOnly/unhandledOnly) membership
  'GroupSlackChannels', // GroupChannelRow: no id, unpatchable
  'GroupsOverviewList', // left-rail counters: no id, unpatchable
  // The detail cards' conversation lists. They ARE normalizable, so the
  // payload usually repaints them for free — but past CONV_PAYLOAD_CAP the
  // payload is truncated and only a refetch corrects the tail.
  //
  // ⚠️ ЭТО ИМЕНА ОПЕРАЦИЙ, А НЕ ПОЛЕЙ. Apollo сверяет строку с `queryName`
  // смонтированных запросов и на промах молчит. В ТЗ 0031 эти три операции
  // переименовали в `*Paged`, а строки здесь остались прежними — рефетч
  // полгода был тихим no-op. Держится тестом `useBulkTriage.queries.test.ts`,
  // который сверяет каждую строку с реальными документами.
  'StudentConversationsPaged',
  'GroupConversationsPaged',
  'TeacherConversationsPaged',
];

/**
 * Имена, которые массовая обработка перечитывает по имени: агрегаты выше
 * плюс счётчики рейки. Без повторов: `GroupsOverviewList` есть в обоих
 * списках. Счётчики меню добавляет `bulkTriageRefetch()`.
 */
export const BULK_TRIAGE_REFETCH: string[] = Array.from(
  new Set([...BULK_AGGREGATE_QUERIES, ...COUNTER_AGGREGATE_QUERIES]),
);

/** Всё, что перечитывает массовая обработка, — со счётчиками меню (ТЗ 0096). */
export const bulkTriageRefetch = (): TCounterRefetch[] => [
  ...BULK_TRIAGE_REFETCH,
  ...myCommsCountersRefetch(),
];

/**
 * Ответ в диалог, который что-то погасил (UX-пакет 0095–0101: ответ =
 * прочитано и обработано то, что менеджер видел): то же, что после массовой
 * обработки, плюс значки студентов группы — `groupCommsIndex` агрегат без id,
 * ответом мутации не патчится. Тред сюда не входит: его рефетч схлопнул бы
 * догруженные страницы (ТЗ 0033).
 */
export const REPLY_TRIAGE_AGGREGATE_QUERIES: string[] = [
  ...BULK_TRIAGE_REFETCH,
  'GroupCommsIndexBadges',
];

/** `REPLY_TRIAGE_AGGREGATE_QUERIES` со счётчиками меню. */
export const replyTriageRefetch = (): TCounterRefetch[] => [
  ...REPLY_TRIAGE_AGGREGATE_QUERIES,
  ...myCommsCountersRefetch(),
];
